/**
 * S5 — per-term deep dive. One tracked term, every dimension the plan §5 asks
 * for, in the markets-terminal register:
 *   1. is it rising?  — the multi-agenda trend chart (uploads + events marked)
 *   2. where is it landing? — geo snapshot (indicative for media; §6.5)
 *   3. how is it framed / where does it stand? — SEPARATE stance + frame splits
 *      (never merged; frame is low-confidence per S2b, flagged as such)
 *   4. what spiked / candidate influence — term-scoped feeds
 *
 * Tone shown only as a low-confidence sidebar; window-position (S8) not yet
 * built — a placeholder makes the missing dimension honest.
 */
import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { api, apiSalience } from "../lib/api";
import type {
  SalienceInfluence,
  SalienceMetricPoint,
  SalienceSpike,
} from "../lib/api";
import { SALIENCE_AGENDA_BLURB, SALIENCE_AGENDA_LABELS } from "../lib/api";
import { ShareTrendChart } from "../components/salience/ShareTrendChart";
import { CoverageHeadlines } from "../components/salience/CoverageHeadlines";
import { SpikeFeed } from "../components/salience/SpikeFeed";
import { InfluenceCandidates } from "../components/salience/InfluenceCandidates";
import { Freshness } from "../components/salience/Freshness";
import { Panel } from "../components/salience/Panel";
import {
  FourAgendasCallout,
  StanceCallout,
  FrameCallout,
  GeoCallout,
  ToneCallout,
  InfluenceCallout,
  CoOccurrenceNote,
} from "../components/salience/explainers";
import {
  TerminalBars,
  STANCE_LABELS,
  STANCE_COLOURS,
  FRAME_LABELS,
} from "../components/salience/TerminalBars";
import {
  DIRECTION_ARROW,
  DIRECTION_CLASS,
  direction,
  formatAgendaValue,
  formatAgendaVelocity,
  formatShare,
  formatVolume,
  GEO_LABELS,
  metricValue,
} from "../components/salience/format";

const KIND_LABELS: Record<string, string> = {
  topic: "Topic",
  tagline: "Tagline",
  entity: "Entity",
};
// Search is no longer its own tile — it's folded into the combined Public tile
// (search interest + YouTube discussion), so the top row is the three academic
// agendas. We still compute the latest SEARCH point separately to feed that
// tile's Search sub-row.
const TILE_ORDER = ["media", "public", "elite"] as const;
const LATEST_AGENDAS = ["media", "search", "public", "elite"] as const;

export function SalienceTermDetail() {
  const { termId = "" } = useParams<{ termId: string }>();

  const overviewQ = useQuery({
    queryKey: ["salience", "overview"],
    queryFn: apiSalience.overview,
  });
  const termsQ = useQuery({
    queryKey: ["salience", "terms"],
    queryFn: () => apiSalience.terms(),
  });
  const pointsQ = useQuery({
    queryKey: ["salience", "timeseries", termId],
    queryFn: () => apiSalience.timeseries({ term: termId }),
    enabled: !!termId,
  });
  const eventsQ = useQuery({
    queryKey: ["salience", "events", termId],
    queryFn: () => apiSalience.events({ term: termId }),
    enabled: !!termId,
  });
  const geoQ = useQuery({
    queryKey: ["salience", "geo", termId],
    queryFn: () => apiSalience.geo(termId),
    enabled: !!termId,
  });
  const calendarQ = useQuery({
    queryKey: ["salience", "calendar"],
    queryFn: () => apiSalience.calendar(),
  });
  const videosQ = useQuery({ queryKey: ["videos"], queryFn: api.videos });

  const termMeta = overviewQ.data?.terms[termId];
  const points = pointsQ.data ?? [];
  const spikes = (eventsQ.data ?? []).filter(
    (e): e is SalienceSpike => e.kind === "spike",
  );
  const influence = (eventsQ.data ?? []).filter(
    (e): e is SalienceInfluence => e.kind === "influence",
  );

  // Fall back to the registry for label/kind when the term has no metric series
  // yet (so a quiet entity shows "Gabriel Zucman", not "gabriel_zucman").
  const registryTerm = termsQ.data?.find((t) => t.term_id === termId);
  const label = termMeta?.label ?? registryTerm?.label ?? termId;
  const kind = termMeta?.kind ?? registryTerm?.kind ?? "";

  // Latest point per agenda (prefer worldwide), for the headline tiles.
  const agendaLatest = useMemo(() => {
    const out: Record<string, SalienceMetricPoint | undefined> = {};
    for (const agenda of LATEST_AGENDAS) {
      const ap = points.filter((p) => p.agenda === agenda);
      if (!ap.length) continue;
      const preferred =
        ap.filter((p) => p.geo === "worldwide" || p.geo === "GB");
      const pool = preferred.length ? preferred : ap;
      out[agenda] = pool.reduce((a, b) =>
        a.bucketDate >= b.bucketDate ? a : b,
      );
    }
    return out;
  }, [points]);

  // Stance / frame aggregated across the term's item-level (public) series.
  const { stance, frame, toneMean, codedItems } = useMemo(() => {
    const stanceAcc: Record<string, number> = {};
    const frameAcc: Record<string, number> = {};
    let toneSum = 0;
    let toneN = 0;
    let coded = 0;
    for (const p of points) {
      for (const [k, v] of Object.entries(p.stanceSplit ?? {}))
        stanceAcc[k] = (stanceAcc[k] ?? 0) + v;
      for (const [k, v] of Object.entries(p.frameSplit ?? {}))
        frameAcc[k] = (frameAcc[k] ?? 0) + v;
      if (p.toneMean != null && p.toneScale === "item") {
        toneSum += p.toneMean * (p.codedItems || 1);
        toneN += p.codedItems || 1;
      }
      coded += p.codedItems || 0;
    }
    return {
      stance: stanceAcc,
      frame: frameAcc,
      toneMean: toneN ? toneSum / toneN : null,
      codedItems: coded,
    };
  }, [points]);

  // Geo snapshot — show media (where geo is meaningful), share-valued bars.
  const mediaGeo = useMemo(() => {
    const m: Record<string, number> = {};
    for (const p of geoQ.data ?? [])
      if (p.agenda === "media" && p.share != null)
        m[GEO_LABELS[p.geo] ?? p.geo] = p.share;
    return m;
  }, [geoQ.data]);

  const noData = !pointsQ.isLoading && points.length === 0;

  return (
    <div>
      <header className="mb-6">
        <Link
          to="/salience"
          className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-ink-muted hover:text-ink"
        >
          <ArrowLeft size={14} /> Salience monitor
        </Link>
        <div className="flex items-center gap-2">
          <span className="rounded bg-black/5 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
            {KIND_LABELS[kind] ?? kind}
          </span>
        </div>
        <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-ink">
          {label}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-muted">
          Everything we track for this term: how its attention is trending across
          the conversations we track, how it is being framed and argued, and where
          coverage is landing.
        </p>
        {registryTerm?.topic_scoped && (
          <div className="mt-1.5 max-w-2xl rounded-lg border border-rule bg-black/2 px-3 py-2">
            <CoOccurrenceNote tone="light" />
          </div>
        )}
        {termMeta && (
          <p className="mt-1 text-sm text-ink-muted">
            Attention across {Object.keys(termMeta.agendas).length} agenda
            {Object.keys(termMeta.agendas).length === 1 ? "" : "s"} ·{" "}
            {formatVolume(codedItems)} coded observations in the live window.
          </p>
        )}
      </header>

      {noData ? (
        <Panel title="No series yet">
          <p className="text-sm text-terminal-ink-muted">
            No metric points for {label} in the live window. The daily score
            pass writes these once the collectors surface relevant items.
          </p>
        </Panel>
      ) : (
        <>
          {/* Headline tiles per agenda */}
          <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <p className="text-[11px] text-ink-muted">
              Each tile is one of the conversations we track, with its latest
              level and recent direction. Public combines search interest and
              YouTube discussion.
            </p>
            <FourAgendasCallout tone="light" />
          </div>
          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {TILE_ORDER.map((agenda) =>
              agenda === "public" ? (
                <PublicAgendaTile
                  key={agenda}
                  search={agendaLatest.search}
                  youtube={agendaLatest.public}
                />
              ) : (
                <AgendaTile
                  key={agenda}
                  agenda={agenda}
                  point={agendaLatest[agenda]}
                />
              ),
            )}
          </div>

          {/* Trend chart */}
          <div className="mb-6">
            <ShareTrendChart
              termId={termId}
              termLabel={label}
              points={points}
              spikes={spikes}
              calendar={calendarQ.data ?? []}
              uploads={videosQ.data ?? []}
              defaultAgenda={kind === "topic" ? "media" : "public"}
            />
          </div>

          {/* Media coverage provenance — the headlines behind the GDELT share */}
          <div className="mb-6">
            <Panel
              title="Recent media coverage"
              eyebrow="GDELT headlines"
              hint="The top news articles behind this term's coverage share, so a jump in the media line explains itself."
            >
              <CoverageHeadlines termId={termId} />
            </Panel>
          </div>

          {/* Stance + frame + geo */}
          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Panel
              title="Stance"
              eyebrow="Toward the wealth-tax position"
              hint="Is the text arguing for or against a wealth tax?"
              info={<StanceCallout tone="dark" />}
            >
              <TerminalBars
                counts={stance}
                labelMap={STANCE_LABELS}
                colourMap={STANCE_COLOURS}
                emptyMessage="No item-level stance yet (media aggregates carry none)."
              />
              {toneMean != null && (
                <div className="mt-3 border-t border-white/10 pt-2 text-[11px] text-terminal-ink-muted">
                  <div>
                    Tone (low-confidence, −1 to +1):{" "}
                    <span className="font-mono tabular-nums text-terminal-ink">
                      {toneMean.toFixed(2)}
                    </span>
                  </div>
                  <div className="mt-1.5">
                    <ToneCallout tone="dark" />
                  </div>
                </div>
              )}
            </Panel>

            <Panel
              title="Frame"
              eyebrow="Competing problem-definitions"
              hint="Which angle the argument takes. A rough steer, not a precise figure."
              info={<FrameCallout tone="dark" />}
            >
              <TerminalBars
                counts={frame}
                labelMap={FRAME_LABELS}
                accent="#c084fc"
                emptyMessage="No item-level frame coded yet."
              />
            </Panel>

            <Panel
              title="Where it's landing"
              eyebrow="Media coverage share by geo"
              hint="Where coverage is landing: the UK against the rest of the world."
              info={<GeoCallout tone="dark" />}
            >
              <TerminalBars
                counts={mediaGeo}
                format={formatShare}
                accent="#4f8cff"
                emptyMessage="No media geo snapshot for this term."
              />
            </Panel>
          </div>

          {/* Spikes + influence */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel title="Spikes" eyebrow="Anomalies for this term">
              <SpikeFeed
                spikes={spikes}
                termLabels={{ [termId]: label }}
                emptyMessage="No spikes for this term in the current window."
              />
            </Panel>
            <Panel
              title="Candidate influence"
              eyebrow="Lead-lag"
              hint="Where this term's attention in one conversation tends to rise just before another."
              info={<InfluenceCallout tone="dark" />}
            >
              <InfluenceCandidates
                items={influence}
                termLabels={{ [termId]: label }}
                emptyMessage="No lead-lag pairs for this term yet. Needs activity in two conversations."
              />
            </Panel>
          </div>

          {/* S8 placeholder — honest about the unbuilt dimension. */}
          <div className="mt-6 rounded-xl border border-dashed border-white/12 bg-terminal/40 p-4 text-[11px] leading-relaxed text-terminal-ink-muted">
            <span className="font-semibold text-terminal-ink-muted">
              Is it becoming mainstream?
            </span>{" "}
            Whether the wealth-tax position is drifting from{" "}
            <span className="italic">fringe</span> toward{" "}
            <span className="italic">mainstream</span> in how it is discussed is a
            planned dimension that is not built yet. It will appear here once the
            acceptability classifier ships and passes its reliability check.
          </div>
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function AgendaTile({
  agenda,
  point,
}: {
  agenda: string;
  point: SalienceMetricPoint | undefined;
}) {
  const value = point ? metricValue(point, agenda) : null;
  const dir = direction(point?.velocity);
  const valueStr = value == null ? "—" : formatAgendaValue(value, agenda);
  const velStr = point ? formatAgendaVelocity(point.velocity, agenda) : "";

  return (
    <div className="rounded-xl border border-white/10 bg-terminal-panel p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-terminal-ink-muted">
          {SALIENCE_AGENDA_LABELS[agenda]}
        </span>
        {point && (
          <span className="font-mono text-[11px] uppercase tracking-wider text-terminal-ink-muted">
            {GEO_LABELS[point.geo] ?? point.geo}
          </span>
        )}
      </div>
      <div className="mt-2 font-mono text-2xl font-bold leading-none tabular-nums text-terminal-ink">
        {valueStr}
      </div>
      {point ? (
        <div
          className={`mt-1 font-mono text-xs font-semibold tabular-nums ${DIRECTION_CLASS[dir]}`}
        >
          {DIRECTION_ARROW[dir]} {velStr}
        </div>
      ) : (
        <div className="mt-1 text-xs text-terminal-ink-muted">No signal yet</div>
      )}
      {point && (
        <Freshness agenda={agenda} date={point.bucketDate} className="mt-2" />
      )}
      <div className="mt-2 text-[10px] leading-snug text-terminal-ink-muted/80">
        {SALIENCE_AGENDA_BLURB[agenda]}
      </div>
    </div>
  );
}

/**
 * The Public tile carries BOTH public sub-signals — search interest and YouTube
 * discussion — as two self-describing rows rather than one blended number. The
 * two are non-commensurable (a 0–100 Trends index vs a within-sample count), so
 * each keeps its own units, direction and (crucially) its own freshness badge:
 * Search and YouTube go stale for different reasons and on different cadences.
 */
function PublicAgendaTile({
  search,
  youtube,
}: {
  search: SalienceMetricPoint | undefined;
  youtube: SalienceMetricPoint | undefined;
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-terminal-panel p-4">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-terminal-ink-muted">
        {SALIENCE_AGENDA_LABELS.public}
      </span>
      <div className="mt-0.5 text-[10px] leading-snug text-terminal-ink-muted/80">
        Search interest + YouTube discussion
      </div>
      <PublicSubRow label="Search" agenda="search" point={search} />
      <div className="my-2.5 border-t border-white/10" />
      <PublicSubRow label="YouTube" agenda="public" point={youtube} />
    </div>
  );
}

function PublicSubRow({
  label,
  agenda,
  point,
}: {
  label: string;
  agenda: string;
  point: SalienceMetricPoint | undefined;
}) {
  const value = point ? metricValue(point, agenda) : null;
  const dir = direction(point?.velocity);
  const valueStr = value == null ? "—" : formatAgendaValue(value, agenda);
  const velStr = point ? formatAgendaVelocity(point.velocity, agenda) : "";

  return (
    <div className="mt-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-terminal-ink-muted">
          {label}
        </span>
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-lg font-bold leading-none tabular-nums text-terminal-ink">
            {valueStr}
          </span>
          {point ? (
            <span
              className={`font-mono text-[11px] font-semibold tabular-nums ${DIRECTION_CLASS[dir]}`}
            >
              {DIRECTION_ARROW[dir]} {velStr}
            </span>
          ) : (
            <span className="text-[11px] text-terminal-ink-muted">
              No signal yet
            </span>
          )}
        </div>
      </div>
      {point && (
        <Freshness agenda={agenda} date={point.bucketDate} className="mt-1" />
      )}
    </div>
  );
}
